import { useEffect, useMemo, useRef, useState } from 'react'
import type { Category, Item, MealTemplate, PlannedMeal, Recipe } from '../../lib/supabase'
import { mealsFromTemplate, templateFromWeek, type TemplateMeal } from '../../lib/mealTemplates'
import { combineNeeds, visibleRecipes, type Need } from '../../lib/recipeBook'
import type { Choice } from '../../lib/choices'
import { dayKey, dayLabel, defaultSelection, fromKey, rangeLabel, weekKeys, weekNumber, weekSpan, weekdayShort } from '../../lib/mealPlan'
import { useLayout } from '../../hooks/useLayout'
import { useReducedMotion } from 'framer-motion'
import { Hero, SegmentBar } from '../list/Hero'
import { SidebarSlot } from '../shell/AppShell'
import { BookIcon, CalendarIcon, CheckIcon, ChevronIcon, PlusIcon, StarIcon } from '../icons'
import { SearchField } from '../ui/SearchField'
import { Cover } from '../recipes/Cover'
import { coverModel } from '../../lib/recipeBook'
import { ActionBar } from '../ActionBar'
import { ChoicesSheet } from '../choices/ChoicesSheet'
import { MealRow } from './MealRow'
import { RecipePicker } from './RecipePicker'
import { DayStrip } from './DayStrip'
import { AnimatePresence } from 'framer-motion'
import { TemplatesSheet } from './TemplatesSheet'

export interface MealPlanTabProps {
  today: Date
  /** Weeks from the current one; 0 = this week. */
  weekOffset: number
  /** The shown week's meals (useMealPlan). */
  meals: PlannedMeal[]
  recipes: Recipe[]
  items: Item[]
  categories: Category[]
  categoryFor: (name: string) => string | null
  /** The household's own name for an ingredient (useItemHistory), so a recipe never adds a second spelling. */
  spellingFor?: (name: string) => string
  /** Names bought before, suggested when a row of the choices sheet is edited (lib/catalog.ts). */
  before?: { name: string }[]
  tintFor: (categoryId: string | null) => string
  /** Signed URLs of the recipes' photos (useRecipePhotos); a recipe without one shows its cover. */
  photoUrls?: Map<string, string>
  locked: boolean
  locale?: string
  onWeek: (offset: number) => void
  onAddMeal: (day: string, recipeId: string, servings: number) => string
  onRemoveMeal: (id: string) => void
  onServings: (id: string, servings: number) => void
  onOpenRecipe: (recipeId: string, servings: number) => void
  onCook: (mealId: string) => void
  /** Add to list for the ticked days (useApplyChoices); `mealIds` get "On list". */
  onApply: (needs: Need[], choices: Choice[], note: string, mealIds: string[]) => void
  /** The household's week templates (useMealTemplates). */
  templates?: MealTemplate[]
  onSaveTemplate?: (name: string, meals: TemplateMeal[]) => void
  onDeleteTemplate?: (id: string) => void
  /** Plans several meals at once with one Undo (a template put on the week); `label` names them. */
  onPlanMeals?: (meals: { day: string; recipe_id: string; servings: number }[], label: string) => void
  onUpgrade: () => void
}

const DEFAULT_SERVINGS = 4
const FLASH_MS = 1600
const DRAG_TYPE = 'application/x-matlist-recipe'

// The Plan tab (feature spec 03): the household's week of meals. Phone: the
// week, a ring of days planned and Templates in one header, the day strip
// (DayStrip) held under the tabs, then an agenda from today to Sunday with the
// days gone by last; an empty day is one line with Add. Desktop: the same
// agenda in a centred column, and a sidebar of recipes — searchable, with
// their photos — to drag onto a day. A day with meals can be ticked
// to shop for (today through Sunday to begin with); the strip's days and Today
// scroll to a day and outline it.
export function MealPlanTab(p: MealPlanTabProps) {
  const layout = useLayout()
  const desktop = layout === 'desktop'
  const reduce = useReducedMotion()
  const todayKey = dayKey(p.today)
  const days = weekKeys(p.today, p.weekOffset)
  const week = weekNumber(fromKey(days[0]))
  const [ticked, setTicked] = useState<Record<string, boolean>>(() => defaultSelection(p.today))
  const [picking, setPicking] = useState<string | null>(null)
  const [shopping, setShopping] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)
  const [flashDay, setFlashDay] = useState<string | null>(null)
  const [todayRequest, setTodayRequest] = useState(0)
  const [query, setQuery] = useState('')
  const found = useMemo(() => visibleRecipes(p.recipes, 'all', null, query), [p.recipes, query])
  const dayRefs = useRef(new Map<string, HTMLElement>())
  const recipeById = useMemo(() => new Map(p.recipes.map(r => [r.id, r])), [p.recipes])
  const recipeIds = useMemo(() => new Set(p.recipes.map(r => r.id)), [p.recipes])
  const mealsOn = (day: string) => p.meals.filter(m => m.day === day && recipeById.has(m.recipe_id))
  const planned = days.filter(d => mealsOn(d).length > 0)
  const mealCount = days.reduce((n, d) => n + mealsOn(d).length, 0)
  // Shopping: the ticked days with meals, and the ticked days between them for
  // the label (an empty day has nothing to tick), at their servings.
  const mealDays = days.filter(d => ticked[d] && mealsOn(d).length > 0)
  const tickedDays = days.filter(d => ticked[d] && d >= mealDays[0] && d <= mealDays[mealDays.length - 1])
  const tickedMeals = mealDays.flatMap(mealsOn)
  const needs = combineNeeds(tickedMeals.map(m => ({ recipe: recipeById.get(m.recipe_id)!, servings: m.servings })), p.spellingFor)
  const shoppingCount = needs.filter(n => !n.pantry).length
  const range = rangeLabel(tickedDays)
  const mealNames = [...new Set(tickedMeals.map(m => recipeById.get(m.recipe_id)!.title))]

  // Going to a day (the strip, Today): bring it into view and outline it
  // briefly. The outline turns on and off from timers, so a later render
  // (another week, another tap) never leaves it stuck on.
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  useEffect(() => () => timers.current.forEach(clearTimeout), [])
  const goTo = (d: string) => {
    dayRefs.current.get(d)?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
    timers.current.forEach(clearTimeout)
    timers.current = [setTimeout(() => setFlashDay(d), 0), setTimeout(() => setFlashDay(null), FLASH_MS)]
  }
  // Today: once this week shows.
  const handled = useRef(0)
  useEffect(() => {
    if (todayRequest === handled.current || p.weekOffset !== 0) return
    handled.current = todayRequest
    goTo(todayKey)
  })

  const guard = (fn: () => void) => () => (p.locked ? p.onUpgrade() : fn())
  const add = (day: string, recipe: Recipe) => {
    p.onAddMeal(day, recipe.id, Math.min(12, recipe.servings ?? DEFAULT_SERVINGS))
    setTicked(t => ({ ...t, [day]: true }))
  }

  const hero = (
    <Hero count={mealCount} label={mealCount === 1 ? 'Meal planned' : 'Meals planned'} layout={layout}>
      <SegmentBar label={`${planned.length} of 7 days planned`}
        segments={{ bar: false, parts: days.map(d => ({ fill: mealsOn(d).length > 0, weight: 1 })) }} />
    </Hero>
  )

  const today = (
    <button type="button" className="ml-btn ml-btn--primary ml-week__today"
      onClick={() => { if (p.weekOffset !== 0) p.onWeek(0); setTodayRequest(n => n + 1) }}>
      <CalendarIcon size={18} /> Today
    </button>
  )

  const phoneHead = (
    <header className="ml-plan-head">
      <div className="ml-plan-head__week">
        <button type="button" className="ml-plan-head__arrow" aria-label="Previous week" onClick={() => p.onWeek(p.weekOffset - 1)}>
          <ChevronIcon size={18} className="ml-week__prev" />
        </button>
        <div className="ml-plan-head__title">
          <h1 className="ml-display ml-plan-head__name">Week {week}</h1>
          <span className="ml-plan-head__dates">{weekSpan(days)}</span>
        </div>
        <button type="button" className="ml-plan-head__arrow" aria-label="Next week" onClick={() => p.onWeek(p.weekOffset + 1)}>
          <ChevronIcon size={18} />
        </button>
      </div>
      <div className="ml-plan-head__tools">
        {p.weekOffset !== 0 && today}
        {p.onPlanMeals && (
          <button type="button" className="ml-round-btn" aria-label="Templates" onClick={() => setShowTemplates(true)}>
            <BookIcon size={18} />
          </button>
        )}
        <WeekRing planned={planned.length} />
      </div>
    </header>
  )

  return (
    <div className={desktop ? 'ml-tab ml-tab--desktop ml-meals' : 'ml-tab ml-meals'}>
      {desktop ? (
        <SidebarSlot>
          {hero}
          <section className="ml-drag-list" aria-label="Recipes">
            <h2 className="ml-label ml-section-label">Recipes <span className="ml-drag-list__hint">Drag onto a day</span></h2>
            <SearchField label="Search recipes" value={query} onChange={setQuery} />
            {found.length === 0 && <p className="ml-caption ml-drag-list__empty">No recipes match</p>}
            <ul>
              {found.map(r => (
                <li key={r.id} aria-label={r.title} className="ml-drag-list__item" draggable
                  onDragStart={e => { e.dataTransfer.setData(DRAG_TYPE, r.id); e.dataTransfer.effectAllowed = 'copy' }}>
                  <button type="button" className="ml-drag-list__open" aria-label={r.title}
                    onClick={() => p.onOpenRecipe(r.id, r.servings ?? DEFAULT_SERVINGS)}>
                    <Cover cover={coverModel(r, p.categoryFor, p.categories)} tintFor={p.tintFor} size="thumb"
                      photoUrl={p.photoUrls?.get(r.id)} />
                    <span className="ml-drag-list__text">
                      <span className="ml-drag-list__name">{r.title}</span>
                      {r.minutes != null && <span className="ml-drag-list__meta">{r.minutes} min</span>}
                    </span>
                    {r.starred && <StarIcon size={16} filled />}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        </SidebarSlot>
      ) : null}
      {phoneHead}

      {!desktop && (
        <DayStrip days={days} todayKey={todayKey} counts={Object.fromEntries(days.map(d => [d, mealsOn(d).length]))} onPick={goTo} />
      )}

      <div className="ml-days">
        {[...days.filter(d => d >= todayKey), ...days.filter(d => d < todayKey)].map(d => {
          const label = dayLabel(d)
          const isToday = d === todayKey
          const meals = mealsOn(d)
          const empty = meals.length === 0
          const on = !!ticked[d] && !empty
          const name = (
            <span className="ml-label ml-day__name">
              {isToday ? <span>Today</span> : weekdayShort(d)}
              <span className="ml-day__sub">{isToday ? label : fromKey(d).getDate()}</span>
            </span>
          )
          const addMeal = (
            <button type="button" className="ml-day__add" aria-label="Add meal" onClick={guard(() => setPicking(d))}>
              <PlusIcon size={16} /> Add
            </button>
          )
          return (
            <section key={d} aria-label={label} ref={el => { if (el) dayRefs.current.set(d, el); else dayRefs.current.delete(d) }}
              className={['ml-day', d < todayKey && 'is-past', on && 'is-ticked', isToday && 'is-today', empty && 'is-empty', flashDay === d && 'is-flash'].filter(Boolean).join(' ')}
              onDragOver={e => { if (!p.locked) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' } }}
              onDrop={e => {
                e.preventDefault()
                const r = recipeById.get(e.dataTransfer.getData(DRAG_TYPE))
                if (r) guard(() => add(d, r))()
              }}>
              {empty ? (
                <div className="ml-day__line">{name}<span className="ml-day__rule" aria-hidden="true" />{addMeal}</div>
              ) : (
                <header className="ml-day__head">
                  {name}
                  <button type="button" className="ml-day__more" aria-label={`Add another meal to ${label}`} onClick={guard(() => setPicking(d))}>
                    <PlusIcon size={16} />
                  </button>
                  {!empty && (
                    <button type="button" role="checkbox" aria-checked={on} aria-label={`Shop for ${label}`}
                      className={`ml-day__tick${on ? ' is-on' : ''}`} onClick={() => setTicked(t => ({ ...t, [d]: !on }))}>
                      {on && <CheckIcon size={14} />}
                    </button>
                  )}
                </header>
              )}
              {!empty && (
              <ul className="ml-day__meals">
                {meals.map(m => {
                  const r = recipeById.get(m.recipe_id)!
                  return (
                    <MealRow key={m.id} meal={m} recipe={r} dayLabel={label} photoUrl={p.photoUrls?.get(r.id)}
                      categories={p.categories} categoryFor={p.categoryFor} tintFor={p.tintFor}
                      onOpen={() => p.onOpenRecipe(r.id, m.servings)}
                      onRemove={guard(() => p.onRemoveMeal(m.id))}
                      onServings={n => (p.locked ? p.onUpgrade() : p.onServings(m.id, n))}
                      onCook={() => p.onCook(m.id)} />
                  )
                })}
              </ul>
              )}
            </section>
          )
        })}
      </div>

      <ActionBar
        selected={tickedMeals.length ? {
          title: range,
          subtitle: `${tickedMeals.length} ${tickedMeals.length === 1 ? 'meal' : 'meals'}, ${shoppingCount} ${shoppingCount === 1 ? 'ingredient' : 'ingredients'}`,
        } : null}
        onAdd={guard(() => setShopping(true))} />

      <ChoicesSheet before={p.before} open={shopping} kicker="Shopping for" title={range} chips={mealNames}
        needs={needs} items={p.items} source="plan" locale={p.locale}
        onApply={(edited, choices) => {
          p.onApply(edited, choices, mealNames.join(', '), tickedMeals.map(m => m.id))
          setShopping(false)
          setTicked(t => Object.fromEntries(Object.entries(t).filter(([d]) => !tickedDays.includes(d))))
        }}
        onClose={() => setShopping(false)} />

      <TemplatesSheet open={showTemplates} weekLabel={`week ${week}`}
        weekMealCount={mealCount} templates={p.templates ?? []} recipeIds={recipeIds} locked={p.locked}
        onSave={name => p.onSaveTemplate?.(name, templateFromWeek(p.meals.filter(m => recipeById.has(m.recipe_id)), days[0]))}
        onUse={t => {
          const meals = mealsFromTemplate(t, days[0], p.meals, recipeIds)
          if (meals.length) {
            p.onPlanMeals?.(meals, t.name)
            setTicked(prev => ({ ...prev, ...Object.fromEntries(meals.filter(m => m.day >= todayKey).map(m => [m.day, true])) }))
          }
          return meals.length
        }}
        onDelete={id => p.onDeleteTemplate?.(id)}
        onUpgrade={() => { setShowTemplates(false); p.onUpgrade() }}
        onClose={() => setShowTemplates(false)} />

      <AnimatePresence>
        {picking && (
          <RecipePicker key="picker" dayLabel={dayLabel(picking)} recipes={p.recipes} categories={p.categories}
            categoryFor={p.categoryFor} tintFor={p.tintFor}
            onPick={r => { add(picking, r); setPicking(null) }} onClose={() => setPicking(null)} />
        )}
      </AnimatePresence>
    </div>
  )
}

// How many days of the shown week have a meal, as a ring (the phone header).
const RING_R = 18
const RING_C = 2 * Math.PI * RING_R
function WeekRing({ planned }: { planned: number }) {
  return (
    <span className="ml-ring" role="img" aria-label={`${planned} of 7 days planned`}>
      <svg viewBox="0 0 44 44" aria-hidden="true">
        <circle className="ml-ring__track" cx="22" cy="22" r={RING_R} />
        <circle className="ml-ring__fill" cx="22" cy="22" r={RING_R} strokeDasharray={`${(planned / 7) * RING_C} ${RING_C}`} />
      </svg>
      <span className="ml-ring__text ml-num">{planned}/7</span>
    </span>
  )
}
